import { format } from 'date-fns'
import { debounce } from 'debounce'
import unionby from 'lodash.unionby'
import { useEffect, useRef, useState } from 'react'
import { SelectInstance } from 'react-select'
import AsyncSelect from 'react-select/async'

import { logisticApi } from 'redux/services/logistic'
import { Deliverymen } from 'redux/services/logistic/types'

import { AppButton } from 'components/AppButton'
import { AppModal } from 'components/AppModal'
import { AppSelect } from 'components/AppSelect'
import { AppTable } from 'components/AppTable'

import { LogisticAct } from 'models/logistic'

import { ReactComponent as CloseIcon } from 'assets/images/svg/close_circle_red.svg'

import styles from './SelectCurier.module.scss'

interface SelectOption {
  label: string
  value: number
}
interface SelectCurierProps {
  onClose: () => void
}
export function SelectCurier({ onClose }: SelectCurierProps) {
  const selectRef = useRef<SelectInstance<SelectOption | null>>(null)
  const [consultant, setConsultant] = useState<Deliverymen>()
  const { data: curiers } = logisticApi.useGetDeliverymensQuery()
  const [handleSearch, handleSearchState] =
    logisticApi.useGetActByBarcodeMutation()
  const [applications, setApplications] = useState<LogisticAct[]>([])

  const [updateSatus, updateSatusState] =
    logisticApi.useAssignConsultantToActMutation()

  const addToList = (arr: LogisticAct[]) => {
    setApplications(prevArr => {
      return unionby(arr, prevArr, 'id')
    })
  }
  const removeFromList = (id: LogisticAct['id']) => {
    setApplications(prevArr => prevArr.filter(item => item.id !== id))
  }
  const onReset = () => {
    setApplications([])
    setConsultant(undefined)
  }
  function handleSubmit() {
    if (!consultant || applications.length === 0) return null

    updateSatus({
      consultant: consultant.user.phone,
      applications: applications.map(c => c.id),
    })
      .unwrap()
      .then(() => {
        onClose()
      })
  }

  useEffect(() => {
    window.addEventListener('keypress', focusInput)
    return () => {
      window.removeEventListener('keypress', focusInput)
    }
  }, [])

  const focusInput = () => {
    selectRef.current?.focus()
  }

  const promiseOptions = (
    inputValue: string,
    callback: (options: SelectOption[]) => void,
  ) => {
    if (inputValue.length > 2) {
      handleSearch(inputValue)
        .unwrap()
        .then(res => {
          callback(
            res
              .filter(r => !applications.some(a => a.id === r.id))
              .map(r => ({
                label: `${r.barcode} - ${r.filial?.name}`,
                value: r.id,
              })),
          )
        })
    } else {
      callback([])
    }
  }
  const loadSuggestions = debounce(promiseOptions, 200)

  const onChange = (value: SelectOption | null) => {
    const selected = handleSearchState.data?.find(r => r.id === value?.value)
    if (selected) {
      addToList([selected])
    }
  }

  const disabled =
    updateSatusState.isLoading || applications.length === 0 || !consultant

  return (
    <>
      <AppModal
        onClose={onClose}
        title={`Назначение на доставку`}
        className={styles.modal}
      >
        <div className={styles.container}>
          <AppSelect
            label="Выбор консультанта"
            size="small"
            text={
              consultant
                ? `${consultant.user.full_name} (${consultant.user.phone})`
                : ''
            }
            onChangeValues={values => {
              if (values[0]) {
                setConsultant(curiers?.find(c => c.id === values[0]))
              }
            }}
            list={
              curiers?.map(c => ({
                title: `${c.user.full_name} (${c.user.phone})`,
                value: c.id,
              })) || []
            }
            required
            withError
          />

          <AsyncSelect
            ref={selectRef}
            autoFocus
            value={null}
            loadOptions={loadSuggestions}
            onChange={onChange}
            placeholder="ID Карты, ФИО или Отсканируйте штрих-код"
            styles={{
              control: (base: any) => ({
                ...base,
                height: 50,
                border: '1px solid #d6d7dc',
                borderRadius: 5,
                padding: '0 0.5rem',
                fontSize: 16,
              }),
            }}
            noOptionsMessage={() => 'Ничего не найдено (введите минимум 3 символа)'}
            loadingMessage={() => 'Поиск...'}
          />
          <br />

          <AppTable
            headers={[
              {
                data: 'ID Акта',
                key: 'barcode',
                width: 20,
              },
              {
                data: 'Дата создания',
                key: 'created_at',
                render: date => format(new Date(date), 'yyyy-MM-dd HH:mm'),
                width: 20,
              },
              {
                data: 'Филиал',
                key: 'filial',
                render: filial => filial?.name,
                width: 20,
              },
              {
                data: 'Количество карт',
                key: 'amount',
                width: 20,
              },
              {
                data: 'Отмена',
                key: 'id',
                render: id => {
                  return (
                    <CloseIcon
                      width={30}
                      height={30}
                      className={styles.delete}
                      onClick={() => removeFromList(id)}
                    />
                  )
                },
                width: 20,
              },
            ]}
            dataSource={applications}
          />

          <div className={styles.controls}>
            <AppButton variant="red" type="button" onClick={onReset}>
              Сбросить
            </AppButton>
            <AppButton
              disabled={disabled}
              type="submit"
              variant="siren"
              onClick={handleSubmit}
            >
              Назначить
            </AppButton>
          </div>
        </div>
      </AppModal>
    </>
  )
}
